import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const HISTOGRAM_THRESHOLD = 20;
const NUM_BINS = 12;

/**
 * Performance chart for a drive model.
 *
 * - ≤ HISTOGRAM_THRESHOLD benchmarks: grouped bar chart (one pair per benchmark)
 * - > HISTOGRAM_THRESHOLD benchmarks: histogram showing frequency distribution
 */
const DrivePerformanceChart = ({ benchmarks, palette }) => {
  const chartBackground =
    palette?.chartBg
    || (document.documentElement.getAttribute('data-theme') === 'dark' ? '#333333' : 'white');

  // Plugin to draw an opaque background behind the chart — theme-aware
  const bgPlugin = useMemo(() => ({
    id: 'customCanvasBackground',
    beforeDraw: (chart) => {
      const { ctx } = chart;
      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = chartBackground;
      ctx.fillRect(0, 0, chart.width, chart.height);
      ctx.restore();
    },
  }), [chartBackground]);
  const { chartData, chartOptions, isHistogram } = useMemo(() => {
    if (!benchmarks || benchmarks.length === 0) {
      return { chartData: null, chartOptions: null, isHistogram: false };
    }

    const reads = benchmarks.map(b => b.readMBps).filter(v => v > 0);
    const writes = benchmarks.map(b => b.writeMBps).filter(v => v > 0);

    if (benchmarks.length <= HISTOGRAM_THRESHOLD) {
      // --- Grouped bar chart: one bar per benchmark ---
      const labels = benchmarks.map((b, i) => {
        if (b.date) {
          const d = new Date(b.date);
          return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' });
        }
        return `#${i + 1}`;
      });

      return {
        isHistogram: false,
        chartData: {
          labels,
          datasets: [
            {
              label: 'Read (MB/s)',
              data: benchmarks.map(b => b.readMBps),
              backgroundColor: palette?.readSample ? `${palette.readSample}B3` : 'rgba(54, 162, 235, 0.7)',
              borderColor: palette?.readSample || 'rgba(54, 162, 235, 1)',
              borderWidth: 1,
            },
            {
              label: 'Write (MB/s)',
              data: benchmarks.map(b => b.writeMBps),
              backgroundColor: palette?.writeSample ? `${palette.writeSample}B3` : 'rgba(255, 159, 64, 0.7)',
              borderColor: palette?.writeSample || 'rgba(255, 159, 64, 1)',
              borderWidth: 1,
            },
          ],
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',
          plugins: {
            title: {
              display: true,
              text: 'Benchmark Results',
              font: { size: 16 },
            },
            legend: { position: 'top' },
            tooltip: {
              callbacks: {
                afterLabel: (ctx) => {
                  const bm = benchmarks[ctx.dataIndex];
                  const parts = [];
                  if (bm.profile) parts.push(`Profile: ${bm.profile}`);
                  if (bm.username) parts.push(`User: ${bm.username}`);
                  return parts.join('\n');
                },
              },
            },
          },
          scales: {
            x: {
              title: { display: true, text: 'MB/s' },
              beginAtZero: true,
            },
            y: {
              ticks: { font: { size: 11 } },
            },
          },
        },
      };
    }

    // --- Histogram: frequency distribution ---
    const allValues = [...reads, ...writes];
    if (allValues.length === 0) {
      return { chartData: null, chartOptions: null, isHistogram: false };
    }
    const maxVal = Math.max(...allValues);
    const minVal = Math.min(...allValues);
    const binWidth = Math.ceil((maxVal - minVal) / NUM_BINS) || 1;
    const binStart = Math.floor(minVal / binWidth) * binWidth;

    const readBins = new Array(NUM_BINS).fill(0);
    const writeBins = new Array(NUM_BINS).fill(0);
    const labels = [];

    for (let i = 0; i < NUM_BINS; i++) {
      const lo = binStart + i * binWidth;
      const hi = lo + binWidth;
      labels.push(`${lo}–${hi}`);
    }

    for (const v of reads) {
      const idx = Math.min(Math.floor((v - binStart) / binWidth), NUM_BINS - 1);
      readBins[idx]++;
    }
    for (const v of writes) {
      const idx = Math.min(Math.floor((v - binStart) / binWidth), NUM_BINS - 1);
      writeBins[idx]++;
    }

    return {
      isHistogram: true,
      chartData: {
        labels,
        datasets: [
          {
            label: 'Read',
            data: readBins,
            backgroundColor: palette?.readSample ? `${palette.readSample}B3` : 'rgba(54, 162, 235, 0.7)',
            borderColor: palette?.readSample || 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
          },
          {
            label: 'Write',
            data: writeBins,
            backgroundColor: palette?.writeSample ? `${palette.writeSample}B3` : 'rgba(255, 159, 64, 0.7)',
            borderColor: palette?.writeSample || 'rgba(255, 159, 64, 1)',
            borderWidth: 1,
          },
        ],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Performance Distribution',
            font: { size: 16 },
          },
          legend: { position: 'top' },
        },
        scales: {
          x: {
            title: { display: true, text: 'MB/s' },
          },
          y: {
            title: { display: true, text: 'Count' },
            beginAtZero: true,
            ticks: { stepSize: 1 },
          },
        },
      },
    };
  }, [benchmarks, palette]);

  if (!chartData) {
    return (
      <div className='text-muted text-center py-4'>
        No benchmark data available for this drive.
      </div>
    );
  }

  const chartHeight = !isHistogram
    ? Math.max(200, benchmarks.length * 40 + 80)
    : 350;

  return (
    <div style={{ height: chartHeight, position: 'relative' }}>
      <Bar
        key={`drive-chart-${chartBackground}`}
        data={chartData}
        options={chartOptions}
        plugins={[bgPlugin]}
      />
    </div>
  );
};

export default DrivePerformanceChart;
